<template>
    <div class="report-renderer" ref="reportContainer" :style="accentStyle">
        <div class="rr-cover" :class="{ 'rr-cover--nosummary': !reportSummary }" ref="coverSlide">
            <div class="rr-hero">
                <div class="rr-hero-main">
                    <h1 class="rr-hero-title">{{ report.name }}</h1>
                    <div class="rr-hero-meta">
                        <span v-if="project && project.title" class="rr-hero-project">{{ project.title }}</span>
                        <span class="rr-hero-sep" v-if="project && project.title">·</span>
                        <span>{{ dateRangeLabel }}</span>
                    </div>
                    <div class="rr-hero-chips">
                        <span v-for="key in heroPlatformKeys" :key="key" class="rr-platform-logo" v-html="platformLogo(key)" :title="platformLabel(key)"></span>
                    </div>
                </div>
                <div class="rr-hero-right">
                    <img v-if="brandLogo" :src="brandLogo" class="rr-hero-logo" alt="" />
                    <div class="rr-hero-brand" v-if="project && project.brand && project.brand.brand_name">
                        <span class="rr-hero-brand-label">Brand</span>
                        <span class="rr-hero-brand-name">{{ project.brand.brand_name }}</span>
                    </div>
                </div>
            </div>

            <div v-if="reportSummary" class="rr-summary-slide">
                <div class="rr-summary-head">
                    <span class="rr-summary-label">Executive Summary</span>
                    <img v-if="brandLogo" :src="brandLogo" class="rr-slide-logo" alt="" />
                </div>
                <p class="rr-summary-text">{{ reportSummary }}</p>
            </div>
        </div>

        <div v-for="(slide, si) in slides" :key="'slide-' + si" class="rr-slide" :ref="'slide-' + si">
            <div v-if="brandLogo" class="rr-slide-brandrow">
                <img :src="brandLogo" class="rr-slide-logo" alt="" />
            </div>
            <div v-for="section in slide" :key="section.id" class="rr-section" :data-section-type="section.type">
                <div class="rr-section-head">
                    <div>
                        <h2 class="rr-section-title">
                            {{ meta(section.type).title || meta(section.type).label }}
                            <span class="rr-section-scope">
                                <span v-if="platformLogo(section.platform)" class="rr-platform-logo rr-platform-logo--sm" v-html="platformLogo(section.platform)"></span>{{ scopeLabel(section) }}
                            </span>
                        </h2>
                        <div v-if="meta(section.type).subtitle" class="rr-section-sub">{{ meta(section.type).subtitle }}</div>
                    </div>
                    <a v-if="appLink(section.type)" class="rr-section-link" :href="appLink(section.type)" target="_blank" rel="noopener">
                        Open in app
                        <v-icon x-small color="primary">mdi-open-in-new</v-icon>
                    </a>
                </div>
                <div class="rr-section-body">
                    <component
                        :is="componentFor(section.type)"
                        v-if="brandsLoaded"
                        :key="renderKey + '-' + section.id + '-' + sectionOptionsKey(section)"
                        :filters="sectionFilters(section)"
                        :options="section.options || {}"
                        :companyId="companyId"
                        :projectId="reportProjectId"
                        :project="project"
                        :brands="brands"
                        :platformKeys="sectionPlatformKeys(section)"
                        @ready="onSectionReady(section.id)" />
                    <div v-else class="rr-loading"><v-progress-circular indeterminate color="primary" size="26" width="3" /></div>
                </div>
            </div>
        </div>

        <div class="rr-footer">
            <span>{{ footerText }}</span>
        </div>

        <div class="report-actions" v-if="showActions">
            <v-btn color="primary" :loading="pdfLoading" :disabled="!allSectionsReady" @click="downloadPdf">
                <v-icon left>mdi-file-pdf-box</v-icon>{{ allSectionsReady ? "Download PDF" : "Loading data …" }}
            </v-btn>
        </div>
    </div>
</template>

<script>
    import { fetchBrandsAll, getReportPlatformKeys, sectionMeta } from "./reportApi"
    import { getPlatformMeta } from "../../utils/platformMeta"
    import KpiCardsSection from "./sections/KpiCardsSection.vue"
    import TrendChartsSection from "./sections/TrendChartsSection.vue"
    import TopBrandsSection from "./sections/TopBrandsSection.vue"
    import TagPerformanceSection from "./sections/TagPerformanceSection.vue"
    import BrandTableSection from "./sections/BrandTableSection.vue"
    import MentionsOverviewSection from "./sections/MentionsOverviewSection.vue"
    import SourcesSection from "./sections/SourcesSection.vue"
    import SourceUrlsSection from "./sections/SourceUrlsSection.vue"

    const SECTION_COMPONENTS = {
        kpi_cards: KpiCardsSection,
        visibility_trend: TrendChartsSection,
        top_brands_per_platform: TopBrandsSection,
        tag_performance: TagPerformanceSection,
        brand_table: BrandTableSection,
        mentions_overview: MentionsOverviewSection,
        sources_domains: SourcesSection,
        sources_urls: SourceUrlsSection
    }

    export default {
        name: "ReportRenderer",
        props: {
            report: { type: Object, required: true },
            project: { type: Object, default: null },
            companyId: { type: String, required: true },
            showActions: { type: Boolean, default: true }
        },
        data() {
            return {
                brands: [],
                brandsLoaded: false,
                sectionReady: {},
                pdfLoading: false,
                renderKey: 0
            }
        },
        computed: {
            filters() {
                return this.report.filters || {}
            },
            brandLogo() {
                return (this.report.branding && this.report.branding.logoUrl) || null
            },
            reportSummary() {
                return (this.report.summary || "").trim()
            },
            // agency accent color: override Vuetify's primary within the report scope so bars,
            // borders, chapter dividers and links pick it up (charts keep per-brand colors)
            accentStyle() {
                const c = this.report.branding && this.report.branding.primaryColor
                return c ? { "--v-primary-base": c } : {}
            },
            // agencies sending white-labeled reports: hide the QUARO footer once a logo is set
            footerText() {
                const date = this.todayLabel
                return this.brandLogo ? `${this.report.name || "Report"} · ${date}` : `${this.report.name || "Report"} · Generated with QUARO · ${date}`
            },
            reportProjectId() {
                return this.report.project_id
            },
            // every platform the project tracks, for sections scoped to "all"
            platformKeys() {
                return getReportPlatformKeys({ platform: "all" }, this.project)
            },
            // distinct concrete platforms used across the report's sections (for the hero logos).
            // when any section is scoped to "all", show every tracked platform icon (deduped so
            // concrete-platform sections don't add a second copy).
            heroPlatformKeys() {
                const keys = new Set()
                if (this.usesAllPlatforms) {
                    this.platformKeys.forEach((k) => keys.add(k))
                }
                this.enabledSections.forEach((s) => {
                    if (s.platform && s.platform !== "all") keys.add(s.platform)
                })
                return [...keys]
            },
            usesAllPlatforms() {
                return this.enabledSections.some((s) => !s.platform || s.platform === "all")
            },
            dateRangeLabel() {
                const dates = (this.filters.selectedDates || []).filter(Boolean)
                if (!dates.length) return ""
                return dates.length > 1 ? `${dates[0]} – ${dates[dates.length - 1]}` : dates[0]
            },
            todayLabel() {
                return new Date().toISOString().slice(0, 10)
            },
            enabledSections() {
                return (this.report.sections || [])
                    .filter((s) => s.enabled && SECTION_COMPONENTS[s.type])
                    .slice()
                    .sort((a, b) => (a.order || 0) - (b.order || 0))
                    .map((s) => ({ ...s, id: s.id || s.type }))
            },
            // pack sections into slides by weight, but never mix different platform/tag scopes
            // on one slide (a shared slide should read as one coherent context)
            slides() {
                const WEIGHTS = { kpi_cards: 0.4, visibility_trend: 0.6, mentions_overview: 0.6, sources_domains: 0.9, sources_urls: 0.9, brand_table: 0.9 }
                const out = []
                let cur = []
                let fill = 0
                let scopeKey = null
                this.enabledSections.forEach((section) => {
                    const key = (section.platform || "all") + "|" + (section.tag || "")
                    let w = WEIGHTS[section.type] || 1
                    if (section.options && section.options.per_platform && this.sectionPlatformKeys(section).length > 1) w = 1
                    if (cur.length && (key !== scopeKey || fill + w > 1)) {
                        out.push(cur)
                        cur = []
                        fill = 0
                    }
                    scopeKey = key
                    cur.push(section)
                    fill += w
                })
                if (cur.length) out.push(cur)
                return out
            },
            allSectionsReady() {
                return this.brandsLoaded && this.enabledSections.every((s) => this.sectionReady[s.id])
            },
            // stable string of the shared date range; the only input that forces a full reload
            selectedDatesKey() {
                return (this.filters.selectedDates || []).filter(Boolean).join("|")
            }
        },
        watch: {
            // Only the shared date range forces a full reload (every section's data changes).
            // Adding / removing / reordering / toggling sections must NOT refetch existing ones:
            // each section fetches in its own mounted(), keyed by section id + options, so a new
            // section mounts alone while the others keep their instances. Branding, name and
            // summary changes don't touch data at all.
            selectedDatesKey() {
                this.reload()
            },
            // mirror the PDF state to the parent so an external (e.g. header) button
            // can reflect loading / readiness and trigger downloadPdf() via $refs
            allSectionsReady: {
                immediate: true,
                handler(v) {
                    this.$emit("ready-change", v)
                }
            },
            pdfLoading: {
                immediate: true,
                handler(v) {
                    this.$emit("loading-change", v)
                }
            }
        },
        methods: {
            meta(type) {
                return sectionMeta(type) || { label: type }
            },
            componentFor(type) {
                return SECTION_COMPONENTS[type]
            },
            platformLabel(key) {
                return getPlatformMeta(key).label || key
            },
            platformLogo(key) {
                return getPlatformMeta(key).logo || null
            },
            appLink(type) {
                const m = sectionMeta(type)
                if (!m || !this.reportProjectId) return null
                const base = `${window.location.origin}/ai-suite/project/${this.reportProjectId}`
                return m.route ? `${base}/${m.route}` : base
            },
            onSectionReady(id) {
                this.$set(this.sectionReady, id, true)
            },
            // include options in the component key so toggling e.g. "per platform" remounts
            // just that one section (and refetches it) without touching the others
            sectionOptionsKey(section) {
                return section.options
                    ? Object.keys(section.options)
                          .sort()
                          .map((k) => k + ":" + section.options[k])
                          .join(",")
                    : ""
            },
            // each section carries its own platform + optional tag scope
            sectionFilters(section) {
                return {
                    ...this.filters,
                    platform: section.platform || "all",
                    tags: section.tag ? [section.tag] : [],
                    tagsOperator: "or",
                    scopeTag: section.tag || null
                }
            },
            sectionPlatformKeys(section) {
                return section.platform && section.platform !== "all" ? [section.platform] : this.platformKeys
            },
            // small context label shown next to the section title (platform + optional tag)
            scopeLabel(section) {
                const p = !section.platform || section.platform === "all" ? "All Platforms" : this.platformLabel(section.platform)
                return section.tag ? `${p} · ${section.tag}` : p
            },
            async reload() {
                this.sectionReady = {}
                this.brandsLoaded = false
                try {
                    this.brands = await fetchBrandsAll(this.companyId, this.reportProjectId)
                } catch (e) {
                    this.brands = []
                } finally {
                    this.brandsLoaded = true
                    this.renderKey++
                }
            },
            captureBlock(html2canvas, block) {
                // inline SVG logos only carry a viewBox; html2canvas rasterizes them to 0×0
                // canvases (createPattern crash) unless width/height attributes are set
                block.querySelectorAll("svg").forEach((svg) => {
                    if (!svg.getAttribute("width") || !svg.getAttribute("height")) {
                        const r = svg.getBoundingClientRect()
                        if (r.width && r.height) {
                            svg.setAttribute("width", r.width)
                            svg.setAttribute("height", r.height)
                        }
                    }
                })
                // html2canvas fails to copy retina-scaled Chart.js canvases (attribute size
                // != CSS size inside overflow:hidden cards) — swap each canvas for a PNG
                // snapshot in the cloned document. Tag the originals so the clone lookup is
                // exact (the cloned document contains the whole page, incl. foreign canvases).
                const origCanvases = [...block.querySelectorAll("canvas")]
                origCanvases.forEach((c, i) => c.setAttribute("data-pdf-canvas", String(i)))
                return html2canvas(block, {
                    scale: 1.5,
                    useCORS: true,
                    backgroundColor: "#ffffff",
                    onclone: (clonedDoc) => {
                        origCanvases.forEach((orig, i) => {
                            if (!orig.width || !orig.height) return
                            const clone = clonedDoc.querySelector(`canvas[data-pdf-canvas="${i}"]`)
                            if (!clone) return
                            // cloneNode never copies canvas contents and an injected <img> with a
                            // data URL loads async (html2canvas won't wait) — paint synchronously
                            clone.width = orig.width
                            clone.height = orig.height
                            clone.getContext("2d").drawImage(orig, 0, 0)
                        })
                    }
                }).finally(() => origCanvases.forEach((c) => c.removeAttribute("data-pdf-canvas")))
            },
            // Split a block's canvas into slide-height chunks, cutting only at safe row
            // boundaries (table rows, list rows) so no line of content is sliced in half.
            sliceBlockCanvas(block, canvas, maxChunkPx, repeatHeaderSel = null) {
                // slightly too-tall slides are scaled down onto one page instead of leaving
                // a near-empty overflow page with a thin sliver of content
                if (canvas.height <= maxChunkPx * 1.3) return [canvas]
                const blockRect = block.getBoundingClientRect()
                const pxRatio = canvas.height / blockRect.height

                // optional header region (e.g. the matrix column labels) that is repeated on
                // every continuation slide so a sliced table never loses its column headers
                let headerPx = 0
                if (repeatHeaderSel) {
                    const headEl = block.querySelector(repeatHeaderSel)
                    if (headEl) {
                        const hr = headEl.getBoundingClientRect()
                        headerPx = Math.max(0, Math.round((hr.bottom - blockRect.top) * pxRatio))
                    }
                }

                const boundaries = []
                block.querySelectorAll("tr, .rr-tbp-row, .rr-pmo-row").forEach((row) => {
                    const r = row.getBoundingClientRect()
                    boundaries.push(Math.round((r.bottom - blockRect.top) * pxRatio))
                })
                boundaries.sort((a, b) => a - b)
                const chunks = []
                let start = 0
                let firstChunk = true
                while (start < canvas.height) {
                    // continuation slides lose vertical room to the repeated header
                    const withHeader = !firstChunk && headerPx > 0
                    const budget = withHeader ? maxChunkPx - headerPx : maxChunkPx
                    let end = Math.min(start + budget, canvas.height)
                    if (end < canvas.height) {
                        // cut at the lowest row boundary that still fits on this slide
                        const fit = boundaries.filter((b) => b > start + budget * 0.35 && b <= start + budget)
                        if (fit.length) end = fit[fit.length - 1]
                    }
                    const bodyH = end - start
                    const slice = document.createElement("canvas")
                    slice.width = canvas.width
                    slice.height = bodyH + (withHeader ? headerPx : 0)
                    const ctx = slice.getContext("2d")
                    ctx.fillStyle = "#ffffff"
                    ctx.fillRect(0, 0, slice.width, slice.height)
                    let destY = 0
                    if (withHeader) {
                        ctx.drawImage(canvas, 0, 0, canvas.width, headerPx, 0, 0, canvas.width, headerPx)
                        destY = headerPx
                    }
                    ctx.drawImage(canvas, 0, start, canvas.width, bodyH, 0, destY, canvas.width, bodyH)
                    chunks.push(slice)
                    start = end
                    firstChunk = false
                }
                return chunks
            },
            async downloadPdf() {
                this.pdfLoading = true
                try {
                    const html2canvas = (await import("html2canvas")).default
                    const { jsPDF } = await import("jspdf")
                    const el = this.$refs.reportContainer

                    // Chart.js canvases can have an empty pixel buffer even while the chart is
                    // visible (late responsive resize clears the buffer, the image survives only
                    // as a GPU layer). Re-render synchronously so toDataURL/drawImage see pixels.
                    const collectVms = (vm, acc = []) => {
                        acc.push(vm)
                        vm.$children.forEach((c) => collectVms(c, acc))
                        return acc
                    }
                    collectVms(this).forEach((vm) => {
                        const chart = vm.$data && vm.$data._chart
                        if (chart && chart.render) {
                            try {
                                chart.render({ duration: 0, lazy: false })
                            } catch (e) {
                                /* chart may be mid-teardown */
                            }
                        }
                    })

                    // 16:9 slide format (PowerPoint size in mm), one section per slide
                    const slideW = 338.7
                    const slideH = 190.5
                    const margin = 10
                    const maxW = slideW - 2 * margin
                    const maxH = slideH - 2 * margin

                    // wide grid sections (tag matrix, top-brands grid) look broken when a
                    // canvas slice drops the column header onto a continuation slide — scale
                    // them to fit one slide instead of slicing
                    const NO_SLICE = new Set(["top_brands_per_platform", "visibility_trend"])
                    // title + executive summary share one cover page
                    const blocks = [
                        {
                            el: this.$refs.coverSlide || el.querySelector(".rr-cover"),
                            url: this.reportProjectId ? `${window.location.origin}/ai-suite/project/${this.reportProjectId}` : null,
                            noSlice: true
                        }
                    ]
                    this.slides.forEach((slide, si) => {
                        const refEl = (this.$refs["slide-" + si] || [])[0]
                        if (refEl)
                            blocks.push({
                                el: refEl,
                                url: this.appLink(slide[0].type),
                                noSlice: slide.some((s) => NO_SLICE.has(s.type)),
                                // the topic matrix can grow past one slide; slice it across slides
                                // and repeat its column header so each page stays readable
                                repeatHeaderSel: slide.some((s) => s.type === "tag_performance") ? ".rr-matrix thead" : null
                            })
                    })

                    const pdf = new jsPDF({ orientation: "landscape", unit: "mm", format: [slideW, slideH] })
                    pdf.setFontSize(8)
                    pdf.setTextColor(148, 163, 184)
                    let firstPage = true

                    for (const block of blocks) {
                        if (!block.el) continue
                        const canvas = await this.captureBlock(html2canvas, block.el)
                        // px per mm when the canvas is scaled to the full slide width
                        const maxChunkPx = Math.floor((maxH / maxW) * canvas.width)
                        const chunks = block.noSlice ? [canvas] : this.sliceBlockCanvas(block.el, canvas, maxChunkPx, block.repeatHeaderSel)
                        for (const chunk of chunks) {
                            if (!firstPage) pdf.addPage([slideW, slideH], "landscape")
                            firstPage = false
                            let wMm = maxW
                            let hMm = (chunk.height / chunk.width) * wMm
                            if (hMm > maxH) {
                                hMm = maxH
                                wMm = (chunk.width / chunk.height) * hMm
                            }
                            const x = (slideW - wMm) / 2
                            const y = (slideH - hMm) / 2
                            pdf.addImage(chunk.toDataURL("image/jpeg", 0.85), "JPEG", x, y, wMm, hMm)
                            if (block.url) pdf.link(x, y, wMm, Math.min(14, hMm), { url: block.url })
                            pdf.text(this.footerText, slideW / 2, slideH - 4, { align: "center" })
                        }
                    }

                    pdf.save(`${this.report.name || "report"}.pdf`)
                } finally {
                    this.pdfLoading = false
                }
            }
        },
        mounted() {
            this.reload()
        }
    }
</script>

<style>
    /* Shared report look — non-scoped on purpose so section components inherit it. */
    .report-renderer {
        background: transparent;
        padding: 20px 24px;
        max-width: 1180px;
        margin: 0 auto;
    }

    /* slides: the on-screen layout mirrors the exported deck 1:1 */
    .report-renderer .rr-slide {
        background: #fff;
        border-radius: 14px;
        border: 1px solid #e2e8f0;
        box-shadow: 0 6px 22px rgba(15, 23, 42, 0.07);
        padding: 20px 26px 22px;
        margin-bottom: 30px;
        /* match the exported 16:9 slide so short content fills the page instead of
           being letterboxed with big empty bands top & bottom in the PDF */
        min-height: 640px;
        display: flex;
        flex-direction: column;
        aspect-ratio: auto;
    }
    .report-renderer .rr-slide .rr-section + .rr-section {
        margin-top: 16px;
        padding-top: 14px;
        border-top: 1px solid #eef2f7;
    }
    /* each section stretches to share the slide height evenly */
    .report-renderer .rr-slide .rr-section {
        flex: 1 1 auto;
        display: flex;
        flex-direction: column;
        min-height: 0;
    }

    /* hero */
    .report-renderer .rr-cover {
        /* title + executive summary together fill one exported 16:9 page */
        min-height: 640px;
        display: flex;
        flex-direction: column;
        margin-bottom: 30px;
    }
    /* no summary -> don't reserve the full page height, otherwise the cover
       shows a large empty area below the hero */
    .report-renderer .rr-cover--nosummary {
        min-height: 0;
    }
    .report-renderer .rr-cover--nosummary .rr-hero {
        margin-bottom: 0;
    }
    .report-renderer .rr-hero {
        display: flex;
        justify-content: space-between;
        align-items: flex-start;
        gap: 16px;
        padding: 18px 22px;
        margin-bottom: 22px;
        border-radius: 14px;
        background: linear-gradient(120deg, rgba(0, 159, 137, 0.08), rgba(0, 159, 137, 0.02));
        border: 1px solid rgba(0, 159, 137, 0.18);
    }
    .report-renderer .rr-hero-title {
        font-size: 1.45rem;
        font-weight: 700;
        line-height: 1.2;
        margin-bottom: 2px;
    }
    .report-renderer .rr-hero-meta {
        font-size: 0.85rem;
        color: rgba(15, 23, 42, 0.65);
        margin-bottom: 8px;
    }
    .report-renderer .rr-hero-sep {
        margin: 0 6px;
    }
    .report-renderer .rr-hero-chips {
        display: flex;
        align-items: center;
        gap: 4px;
        flex-wrap: wrap;
    }
    .report-renderer .rr-hero-all {
        font-size: 0.75rem;
        font-weight: 600;
        color: var(--v-primary-base);
    }
    .report-renderer .rr-hero-brand {
        text-align: right;
        white-space: nowrap;
    }
    /* executive summary intro slide */
    .report-renderer .rr-summary-slide {
        flex: 1 1 auto;
        background: #fff;
        border: 1px solid #e2e8f0;
        border-left: 4px solid var(--v-primary-base);
        border-radius: 14px;
        box-shadow: 0 6px 22px rgba(15, 23, 42, 0.07);
        padding: 20px 26px 22px;
    }
    .report-renderer .rr-summary-head {
        display: flex;
        align-items: center;
        justify-content: space-between;
        margin-bottom: 10px;
    }
    .report-renderer .rr-summary-label {
        font-size: 0.72rem;
        font-weight: 700;
        text-transform: uppercase;
        letter-spacing: 0.06em;
        color: var(--v-primary-base);
    }
    .report-renderer .rr-summary-text {
        font-size: 1rem;
        line-height: 1.6;
        color: #1f2937;
        white-space: pre-wrap;
        margin: 0;
    }

    /* chapter header on the first slide of each platform/tag scope */
    .report-renderer .rr-scope-head {
        display: flex;
        align-items: center;
        gap: 10px;
        margin: 0 0 14px;
        padding-bottom: 10px;
        border-bottom: 2px solid var(--v-primary-base);
    }
    .report-renderer .rr-scope-logo,
    .report-renderer .rr-scope-logo svg {
        width: 22px;
        height: 22px;
        display: inline-flex;
    }
    .report-renderer .rr-scope-title {
        font-size: 1.15rem;
        font-weight: 700;
        color: #0f172a;
    }
    .report-renderer .rr-scope-tag {
        font-size: 0.75rem;
        font-weight: 700;
        color: #fff;
        background: var(--v-primary-base);
        border-radius: 999px;
        padding: 2px 12px;
    }
    .report-renderer .rr-hero-brand-label {
        display: block;
        font-size: 0.68rem;
        text-transform: uppercase;
        letter-spacing: 0.06em;
        color: rgba(15, 23, 42, 0.5);
    }
    .report-renderer .rr-hero-brand-name {
        font-size: 1rem;
        font-weight: 700;
    }

    /* sections (flat inside slides) */
    .report-renderer .rr-section-head {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 0 0 4px 0;
    }
    .report-renderer .rr-section-title {
        font-size: 1.5rem !important;
        font-weight: 700 !important;
        color: #0f172a;
        line-height: 1.2;
    }
    /* small platform/tag context next to each section title */
    .report-renderer .rr-section-scope {
        display: inline-flex;
        align-items: center;
        gap: 5px;
        margin-left: 10px;
        padding: 3px 11px;
        border-radius: 999px;
        background: rgba(15, 23, 42, 0.06);
        font-size: 0.82rem;
        font-weight: 600;
        color: rgba(15, 23, 42, 0.6);
        vertical-align: middle;
    }
    .report-renderer .rr-section-sub {
        font-size: 0.9rem;
        color: rgba(15, 23, 42, 0.55);
        line-height: 1.3;
        margin-top: 3px;
    }
    /* agency logo branding */
    .report-renderer .rr-slide-logo {
        height: 26px;
        max-width: 160px;
        object-fit: contain;
        display: block;
    }
    .report-renderer .rr-slide-brandrow {
        display: flex;
        justify-content: flex-end;
        margin-bottom: 8px;
    }
    .report-renderer .rr-scope-head-logo {
        margin-left: auto;
    }
    .report-renderer .rr-hero-right {
        display: flex;
        flex-direction: column;
        align-items: flex-end;
        gap: 10px;
    }
    .report-renderer .rr-hero-logo {
        height: 44px;
        max-width: 220px;
        object-fit: contain;
    }
    .report-renderer .rr-section-link {
        font-size: 0.72rem;
        font-weight: 600;
        text-decoration: none;
        color: var(--v-primary-base);
        display: inline-flex;
        align-items: center;
        gap: 3px;
    }
    .report-renderer .rr-section-body {
        padding: 6px 0 0 0;
        flex: 1 1 auto;
        min-height: 0;
    }

    /* generic bits shared by sections */
    .report-renderer .rr-loading {
        display: flex;
        justify-content: center;
        padding: 26px;
    }
    .report-renderer .rr-empty {
        color: rgba(0, 0, 0, 0.45);
        font-size: 0.85rem;
        padding: 8px 0;
    }
    .report-renderer .rr-nowrap {
        white-space: nowrap;
    }
    .report-renderer .rr-platform-logo {
        display: inline-flex;
        width: 20px;
        height: 20px;
        vertical-align: middle;
    }
    .report-renderer .rr-platform-logo svg {
        width: 20px;
        height: 20px;
        border-radius: 4px;
    }
    .report-renderer .rr-platform-logo--sm,
    .report-renderer .rr-platform-logo--sm svg {
        width: 16px;
        height: 16px;
    }

    .report-renderer .rr-delta-chip {
        display: inline-flex;
        align-items: center;
        gap: 1px;
        font-size: 0.68rem;
        font-weight: 600;
        border-radius: 10px;
        padding: 1px 7px;
        margin-left: 6px;
        background: rgba(15, 23, 42, 0.06);
        color: rgba(15, 23, 42, 0.6);
    }
    .report-renderer .rr-delta-chip.up {
        background: rgba(76, 175, 80, 0.14);
        color: #2e7d32;
    }
    .report-renderer .rr-delta-chip.up .v-icon {
        color: #2e7d32;
    }
    .report-renderer .rr-delta-chip.down {
        background: rgba(244, 67, 54, 0.12);
        color: #c62828;
    }
    .report-renderer .rr-delta-chip.down .v-icon {
        color: #c62828;
    }

    .report-renderer .rr-bar {
        display: block;
        height: 6px;
        border-radius: 3px;
        background: rgba(15, 23, 42, 0.08);
        overflow: hidden;
        margin-top: 4px;
    }
    .report-renderer .rr-bar--inline {
        display: inline-block;
        width: 90px;
        vertical-align: middle;
        margin: 0 8px 0 0;
    }
    .report-renderer .rr-bar--cell {
        margin: 3px 0;
    }
    .report-renderer .rr-bar-fill {
        display: block;
        height: 100%;
        border-radius: 3px;
        /* solid color on purpose: html2canvas rasterizes gradient backgrounds via canvas,
           which crashes on zero-width fills during PDF export */
        background: var(--v-primary-base);
    }

    /* KPI cards */
    .report-renderer .rr-kpi-card {
        border-radius: 12px !important;
        padding: 14px 16px 8px 16px;
        height: 100%;
    }
    .report-renderer .rr-kpi-top {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 6px;
    }
    .report-renderer .rr-kpi-label {
        font-size: 0.68rem;
        font-weight: 700;
        letter-spacing: 0.05em;
        text-transform: uppercase;
        color: rgba(15, 23, 42, 0.6);
    }
    .report-renderer .rr-kpi-value {
        font-size: 1.7rem;
        font-weight: 700;
        line-height: 1.1;
    }
    .report-renderer .rr-kpi-value--up {
        color: #2e7d32;
    }
    .report-renderer .rr-kpi-value--down {
        color: #c62828;
    }
    .report-renderer .rr-kpi-sub {
        font-size: 0.7rem;
        color: rgba(15, 23, 42, 0.5);
        margin-top: 2px;
    }
    .report-renderer .rr-kpi-trend {
        margin-top: 8px;
        height: 26px;
    }
    .report-renderer .rr-kpi-trend svg {
        width: 100%;
        height: 100%;
    }
    .report-renderer .rr-kpi-trend-line {
        fill: none;
        stroke: rgba(15, 23, 42, 0.25);
        stroke-width: 2;
    }
    .report-renderer .rr-kpi-trend.up .rr-kpi-trend-line {
        stroke: #4caf50;
    }
    .report-renderer .rr-kpi-trend.down .rr-kpi-trend-line {
        stroke: #ef5350;
    }

    /* top brands per platform */
    .report-renderer .rr-tbp-root {
        height: 100%;
    }
    .report-renderer .rr-tbp-grid {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
        gap: 14px;
        height: 100%;
        align-content: stretch;
    }
    .report-renderer .rr-tbp-card {
        border: 1px solid rgba(15, 23, 42, 0.1);
        border-radius: 12px;
        padding: 12px;
        display: flex;
        flex-direction: column;
    }
    .report-renderer .rr-tbp-rows {
        flex: 1 1 auto;
        display: flex;
        flex-direction: column;
        justify-content: space-between;
        gap: 4px;
    }
    .report-renderer .rr-tbp-head {
        display: flex;
        align-items: center;
        gap: 8px;
        font-weight: 700;
        font-size: 0.86rem;
        margin-bottom: 10px;
    }
    .report-renderer .rr-tbp-row {
        display: flex;
        align-items: center;
        gap: 8px;
        padding: 7px 6px;
        border: 1px solid transparent;
        border-radius: 10px;
    }
    .report-renderer .rr-tbp-row--primary {
        border-color: rgba(0, 159, 137, 0.45);
        background: rgba(0, 159, 137, 0.06);
    }
    .report-renderer .rr-tbp-rank {
        flex: 0 0 30px;
        height: 30px;
        border-radius: 50%;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        font-size: 0.68rem;
        font-weight: 700;
        background: rgba(15, 23, 42, 0.07);
        color: rgba(15, 23, 42, 0.6);
    }
    .report-renderer .rr-rank-1 {
        background: rgba(76, 175, 80, 0.16);
        color: #2e7d32;
    }
    .report-renderer .rr-rank-2 {
        background: rgba(255, 193, 7, 0.2);
        color: #a67c00;
    }
    .report-renderer .rr-rank-3 {
        background: rgba(255, 152, 0, 0.16);
        color: #b25e09;
    }
    .report-renderer .rr-tbp-main {
        flex: 1;
        min-width: 0;
    }
    .report-renderer .rr-tbp-brand {
        font-size: 0.8rem;
        font-weight: 600;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
    }
    .report-renderer .rr-tbp-meta {
        font-size: 0.68rem;
        color: rgba(15, 23, 42, 0.5);
    }
    .report-renderer .rr-tbp-metric {
        flex: 0 0 110px;
        text-align: right;
    }
    .report-renderer .rr-tbp-vis {
        font-size: 0.82rem;
        font-weight: 700;
    }

    /* trend blocks */
    .report-renderer .rr-trend-title {
        display: flex;
        align-items: center;
        gap: 7px;
        font-size: 0.82rem;
        font-weight: 700;
        margin-bottom: 6px;
    }
    /* static legend under the trend chart: which brands/competitors the lines are */
    .report-renderer .rr-legend {
        display: flex;
        flex-wrap: wrap;
        gap: 4px 14px;
        margin-top: 10px;
        padding-top: 8px;
        border-top: 1px solid #eef2f7;
    }
    .report-renderer .rr-legend-item {
        display: inline-flex;
        align-items: center;
        gap: 5px;
        font-size: 0.72rem;
        color: rgba(15, 23, 42, 0.75);
    }
    .report-renderer .rr-legend-swatch {
        width: 11px;
        height: 11px;
        border-radius: 3px;
        flex: 0 0 auto;
    }
    .report-renderer .rr-legend-more {
        font-size: 0.72rem;
        font-style: italic;
        color: rgba(15, 23, 42, 0.5);
    }

    /* tables */
    .report-renderer .rr-table {
        width: 100%;
        border-collapse: collapse;
        font-size: 0.8rem;
    }
    .report-renderer .rr-table th {
        text-align: left;
        font-size: 0.66rem;
        text-transform: uppercase;
        letter-spacing: 0.05em;
        color: rgba(15, 23, 42, 0.5);
        font-weight: 700;
        padding: 6px 10px;
        border-bottom: 1px solid rgba(15, 23, 42, 0.1);
    }
    .report-renderer .rr-table td {
        padding: 8px 10px;
        border-bottom: 1px solid rgba(15, 23, 42, 0.06);
        vertical-align: middle;
    }
    .report-renderer .rr-row--primary td {
        background: rgba(0, 159, 137, 0.05);
    }
    .report-renderer .rr-brand-cell {
        font-weight: 600;
        display: flex;
        align-items: center;
        gap: 7px;
    }
    .report-renderer .rr-brand-dot {
        width: 9px;
        height: 9px;
        border-radius: 50%;
        display: inline-block;
        flex: 0 0 9px;
    }
    .report-renderer .rr-favicon {
        width: 18px;
        height: 18px;
        border-radius: 4px;
        flex: 0 0 18px;
    }
    .report-renderer .rr-rank-plain {
        font-weight: 700;
    }
    .report-renderer .rr-tag-chip {
        display: inline-block;
        font-size: 0.68rem;
        font-weight: 600;
        border: 1px solid rgba(0, 159, 137, 0.4);
        color: var(--v-primary-base);
        border-radius: 10px;
        padding: 0 8px;
        margin: 1px 3px 1px 0;
        white-space: nowrap;
    }

    /* tag matrix */
    .report-renderer .rr-matrix-wrap {
        overflow-x: auto;
    }
    .report-renderer .rr-matrix {
        width: 100%;
        border-collapse: collapse;
        font-size: 0.76rem;
    }
    .report-renderer .rr-matrix th {
        text-align: left;
        font-size: 0.66rem;
        text-transform: uppercase;
        letter-spacing: 0.04em;
        color: rgba(15, 23, 42, 0.55);
        padding: 6px 8px;
        border-bottom: 1px solid rgba(15, 23, 42, 0.1);
        white-space: nowrap;
    }
    .report-renderer .rr-matrix td {
        padding: 8px;
        border-bottom: 1px solid rgba(15, 23, 42, 0.06);
        vertical-align: top;
        min-width: 130px;
    }
    .report-renderer .rr-matrix-tag-col {
        font-weight: 600;
        min-width: 170px !important;
        max-width: 230px;
    }
    .report-renderer .rr-cell-rank {
        display: inline-flex;
        align-items: center;
        gap: 3px;
        font-size: 0.66rem;
        font-weight: 700;
        border-radius: 9px;
        padding: 1px 8px;
        margin-bottom: 4px;
        background: rgba(15, 23, 42, 0.07);
        color: rgba(15, 23, 42, 0.55);
    }
    .report-renderer .rr-rank-cell--1 {
        background: rgba(76, 175, 80, 0.16);
        color: #2e7d32;
    }
    .report-renderer .rr-rank-cell--1 .v-icon {
        color: #2e7d32;
    }
    .report-renderer .rr-rank-cell--2 {
        background: rgba(255, 193, 7, 0.22);
        color: #a67c00;
    }
    .report-renderer .rr-rank-cell--2 .v-icon {
        color: #a67c00;
    }
    .report-renderer .rr-rank-cell--3 {
        background: rgba(255, 152, 0, 0.18);
        color: #b25e09;
    }
    .report-renderer .rr-rank-cell--3 .v-icon {
        color: #b25e09;
    }
    .report-renderer .rr-cell-line {
        display: flex;
        justify-content: space-between;
        gap: 8px;
        line-height: 1.5;
    }
    .report-renderer .rr-cell-key {
        color: rgba(15, 23, 42, 0.5);
        font-size: 0.68rem;
        text-transform: uppercase;
    }
    .report-renderer .rr-cell-val {
        font-weight: 700;
    }

    /* platform mentions overview */
    .report-renderer .rr-pmo-head,
    .report-renderer .rr-pmo-row {
        display: grid;
        grid-template-columns: 180px 1fr 1fr 1fr;
        gap: 14px;
        align-items: center;
        padding: 7px 4px;
    }
    .report-renderer .rr-pmo-head {
        font-size: 0.66rem;
        text-transform: uppercase;
        letter-spacing: 0.05em;
        color: rgba(15, 23, 42, 0.5);
        font-weight: 700;
        border-bottom: 1px solid rgba(15, 23, 42, 0.1);
    }
    .report-renderer .rr-pmo-row {
        border-bottom: 1px solid rgba(15, 23, 42, 0.05);
    }
    .report-renderer .rr-pmo-col--platform {
        display: flex;
        align-items: center;
        gap: 7px;
        font-size: 0.8rem;
        font-weight: 600;
    }
    .report-renderer .rr-pmo-count {
        display: inline-block;
        min-width: 26px;
        font-size: 0.78rem;
        font-weight: 700;
        margin-right: 6px;
    }
    .report-renderer .rr-pmo-bar {
        display: inline-block;
        width: calc(100% - 40px);
        height: 7px;
        border-radius: 4px;
        background: rgba(15, 23, 42, 0.07);
        vertical-align: middle;
        overflow: hidden;
    }
    .report-renderer .rr-pmo-bar-fill {
        display: block;
        height: 100%;
        border-radius: 4px;
    }
    .report-renderer .rr-pmo-bar-fill--total {
        background: #64748b;
    }
    .report-renderer .rr-pmo-bar-fill--new {
        background: #2e9e46;
    }
    .report-renderer .rr-pmo-bar-fill--lost {
        background: #d33c3c;
    }

    /* footer + actions */
    .report-renderer .rr-footer {
        text-align: center;
        font-size: 0.7rem;
        color: rgba(15, 23, 42, 0.4);
        padding: 8px 0 4px 0;
    }
    .report-renderer .report-actions {
        display: flex;
        justify-content: flex-end;
        margin-top: 14px;
    }
</style>
